/**
 * Helpers centralizados para conversão entre horas decimais e o formato HH:MM.
 * Usados pelo componente InputHoraHHMM e telas do módulo de tarefas.
 */

/**
 * Converte horas decimais (ex: 1.5) para o formato HH:MM (ex: "01:30").
 * Retorna string vazia para valores nulos/indefinidos/inválidos.
 */
export const decimalToHHMM = (
  decimal: number | "" | null | undefined
): string => {
  if (decimal === null || decimal === undefined || decimal === "") return "";
  const numDecimal = Number(decimal);
  if (isNaN(numDecimal)) return "";
  const horas = Math.floor(numDecimal);
  const minutos = Math.round((numDecimal - horas) * 60);
  return `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}`;
};

/**
 * Converte minutos inteiros (ex: 90) para o formato HH:MM (ex: "01:30").
 * É a fonte de verdade usada pelas planilhas (TotalMinutos), por isso deve
 * ser usada para exibir o tempo real (totalMinutosReal) nos cards/painéis.
 * Retorna "00:00" para valores nulos/indefinidos/inválidos.
 */
export const minutosToHHMM = (
  minutos: number | null | undefined
): string => {
  if (minutos === null || minutos === undefined) return "00:00";
  const total = Math.max(0, Math.round(Number(minutos)));
  if (isNaN(total)) return "00:00";
  const horas = Math.floor(total / 60);
  const mins = total % 60;
  return `${String(horas).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

/**
 * Item 6 (Fase 4): converte segundos inteiros para o padrão hh:mm:ss (ex: 3661 → "01:01:01").
 * É a fonte de verdade do tempo REAL cronometrado exibido em tarefas, subtarefas,
 * apontamentos e planilha (decisão do PO em 03/07). Retorna "00:00:00" para valores
 * nulos/indefinidos/inválidos.
 */
export const segundosToHHMMSS = (
  segundos: number | null | undefined
): string => {
  if (segundos === null || segundos === undefined) return "00:00:00";
  const total = Math.max(0, Math.round(Number(segundos)));
  if (isNaN(total)) return "00:00:00";
  const horas = Math.floor(total / 3600);
  const minutos = Math.floor((total % 3600) / 60);
  const segs = total % 60;
  return `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}:${String(segs).padStart(2, "0")}`;
};

/**
 * Converte HH:MM (ex: "01:30") para horas decimais (ex: 1.5).
 * Minutos são limitados a 59. Retorna undefined para entrada vazia/inválida.
 */
export const hhmmToDecimal = (hhmm: string): number | undefined => {
  if (!hhmm || hhmm.trim() === "") return undefined;

  // Remove caracteres não numéricos exceto ":"
  const cleanValue = hhmm.replace(/[^\d:]/g, "");

  // Tenta fazer o parse no formato HH:MM
  const match = cleanValue.match(/^(\d+):?(\d{0,2})$/);
  if (match) {
    const horas = parseInt(match[1], 10) || 0;
    const minutos = parseInt(match[2] || "0", 10) || 0;
    // Limita minutos a 59
    const minutosNormalizados = Math.min(minutos, 59);
    return horas + minutosNormalizados / 60;
  }

  // Se não tem ":", trata como horas
  const horasApenas = parseInt(cleanValue, 10);
  if (!isNaN(horasApenas)) {
    return horasApenas;
  }

  return undefined;
};
